import Image from "next/image";
import { Star } from "lucide-react";
import { useTranslations } from "next-intl";

// A single Google review card. Presentational only — fixed width to match the
// News carousel track, with a thin gradient border frame (p-px trick) and a
// subtle hover lift. Avatar falls back to deterministic initials when no image
// is supplied. `draggable={false}` so the card never hijacks the carousel drag.

// Deterministic avatar background so the same reviewer always gets the same
// colour without needing Math.random (which is unavailable at build time here).
const AVATAR_COLORS = [
  "bg-sky-blue",
  "bg-indigo-500",
  "bg-emerald-500",
  "bg-amber-500",
  "bg-rose-500",
  "bg-violet-500",
];

function initialsOf(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function colorFor(name = "") {
  let sum = 0;
  for (let i = 0; i < name.length; i++) sum += name.charCodeAt(i);
  return AVATAR_COLORS[sum % AVATAR_COLORS.length];
}

export default function ReviewCard({ review, className = "", ...rest }) {
  const t = useTranslations("home.googleReviews");
  // i18n-todo: backend content — name/date/text are real review data (deferred).
  const { name, avatar, rating = 5, date, text } = review ?? {};
  const safeRating = Math.max(0, Math.min(5, Math.round(rating)));

  return (
    <div
      draggable={false}
      className={`w-[300px] shrink-0 rounded-2xl bg-gradient-to-b from-white/20 to-white/5 p-px transition-transform duration-300 hover:-translate-y-1 sm:w-[360px] ${className}`}
      {...rest}
    >
      <article className="flex h-full flex-col gap-4 rounded-2xl bg-neutral-900 p-6">
        {/* Header: avatar + name/date + Google mark */}
        <div className="flex items-center gap-3">
          {avatar ? (
            <Image
              src={avatar}
              alt={name || "reviewer"}
              width={48}
              height={48}
              className="h-12 w-12 rounded-full object-cover"
            />
          ) : (
            <span
              aria-hidden="true"
              className={`grid h-12 w-12 place-items-center rounded-full text-base font-semibold text-white ${colorFor(
                name,
              )}`}
            >
              {initialsOf(name)}
            </span>
          )}

          <div className="min-w-0 flex-1">
            <p className="truncate text-base font-semibold text-white">{name}</p>
            <p className="text-sm font-normal text-white/60">{date}</p>
          </div>

          <Image
            src="/images/2026/google-reviews/google-g.svg"
            alt="Google"
            width={20}
            height={20}
            className="h-5 w-5 shrink-0"
          />
        </div>

        {/* Stars */}
        <div
          className="flex items-center gap-1"
          role="img"
          aria-label={t("starsAria", { count: safeRating })}
        >
          {Array.from({ length: 5 }).map((_, i) => (
            <Star
              key={i}
              className={`h-4 w-4 ${
                i < safeRating
                  ? "fill-amber-400 text-amber-400"
                  : "fill-neutral-700 text-neutral-700"
              }`}
              strokeWidth={0}
            />
          ))}
        </div>

        {/* Review body */}
        <p className="line-clamp-5 text-base font-normal leading-relaxed text-white/80">
          {text}
        </p>
      </article>
    </div>
  );
}
